import type { ThemeUIStyleObject } from '@theme-ui/core';
import { Waypoint } from 'react-waypoint';

import { Separator } from '@change/ds-core-ui/next';

import { Translate } from '@change-corgi/core/react/i18n';
import { Link } from '@change-corgi/design-system-legacy/components/actions';
import { Card } from '@change-corgi/design-system-legacy/components/content';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';
import { iconInfoOutline } from '@change-corgi/design-system-legacy/icons';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import type { WaypointCallbackArgs } from 'src/app/shared/components/layout/lazy';

import { FacebookGroupsVariant } from './components/FacebookGroups/variant';
import { RedditSubredditsVariant } from './components/RedditSubreddits/variant';
import { ShareUrl } from './components/ShareUrl';
import { useRecommendations } from './hooks/useRecommendations';
import { useShareRecommendations } from './hooks/useShareRecommendations';

const styles: Record<string, ThemeUIStyleObject> = {
	betaTag: {
		display: 'flex',
		padding: '2px 8px',
		justifyContent: 'center',
		alignItems: 'center',
	},
	mainCard: {
		border: 'unset',
		width: '100%',
		borderRadius: 'big',
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
	},
};

type Props = {
	slug: string;
};

export function RecommendationsVariant1({ slug }: Props): JSX.Element | null {
	const {
		data: { shareRecommendationsLoading, shouldDisplayShareRecommendations, shareRecommendations },
	} = useShareRecommendations(slug);

	const { actions } = useRecommendations();

	if (shareRecommendationsLoading) {
		return <Loader size="m" my={64} mx="auto" data-testid="share-recommendations-loading" />;
	}

	if (!shouldDisplayShareRecommendations) {
		return null;
	}

	const firstRedditRec = shareRecommendations.reddit.at(0);
	const firstFacebookRec = shareRecommendations.facebook.at(0);

	return (
		<Box as="section" aria-labelledby="recommendations-title">
			<Waypoint
				onPositionChange={(waypointCallbackArgs: WaypointCallbackArgs) => {
					actions.trackView(waypointCallbackArgs, firstFacebookRec?.metadata.source, firstRedditRec?.metadata.source);
				}}
			/>
			<Card p={24} backgroundColor="white" sx={styles.mainCard}>
				<Flex sx={{ gap: '24px', flexDirection: 'column' }}>
					<Flex sx={{ flexDirection: 'column', gap: '16px' }}>
						<Heading as="h2" size="h4" id="recommendations-title" data-qa="recommendations-title">
							Share in recommended groups
						</Heading>
						<Text size="default">
							{actions.translateHtml('corgi.petition.starter_dashboard.recommendations.description_variant')}&nbsp;
							<Link
								variant="primary"
								to="#"
								onClick={async (e) => {
									e.preventDefault();
									await actions.handleTipClick();
								}}
								sx={{ display: 'inline-flex', color: 'primary-black', fontWeight: '400', alignItems: 'center' }}
								data-qa="recommendations-tip-button"
							>
								<Text size="default">
									<Flex sx={{ alignItems: 'center', gap: 3 }}>
										<Translate value="corgi.petition.starter_dashboard.recommendations.sharing_tips" />
										<Icon icon={iconInfoOutline} color="currentColor" size="m" />
									</Flex>
								</Text>
							</Link>
						</Text>
						<div className="max-w-full md:max-w-[50%]">
							<ShareUrl />
						</div>
					</Flex>
					<Separator className="m-0" />
					<Flex
						sx={{
							flexDirection: ['column', 'row'],
							justifyContent: ['space-between'],
							width: '100%',
							gap: ['24px', '40px'],
						}}
					>
						<RedditSubredditsVariant shareRecommendations={shareRecommendations.reddit} />
						{shareRecommendations.reddit.length > 0 && shareRecommendations.facebook.length > 0 && (
							<Separator data-test-id="recommendation-platforms-separator" className="m-0 md:hidden" />
						)}
						<FacebookGroupsVariant shareRecommendations={shareRecommendations.facebook} />
					</Flex>
				</Flex>
			</Card>
		</Box>
	);
}
